import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { ref, update } from 'firebase/database';
import { apiFetch } from '../utils/api';
import SEO from '../components/SEO';

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<any>(null);

  // User info
  const [profession, setProfession] = useState('');
  const [location, setLocation] = useState('');
  const [hearAbout, setHearAbout] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [industry, setIndustry] = useState('');

  // WhatsApp phone verification (step 2)
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
    const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
  const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/register');
      } else {
        setUser(user);
        if (user.phoneNumber) setPhoneNumber(user.phoneNumber);
      }
    });
    return unsubscribe;
  }, [navigate]);

  const professionsList = [
    'Business Owner', 'Marketing Manager', 'Sales Professional',
    'Customer Support', 'Entrepreneur', 'Freelancer',
    'Agency Owner', 'Other'
  ];

  const industriesList = [
    'E-commerce', 'Retail', 'Real Estate', 'Education',
    'Healthcare', 'Hospitality', 'Finance', 'Technology',
    'Manufacturing', 'Non-profit', 'Other'
  ];

  const hearAboutList = [
    'Google Search', 'Facebook/Instagram', 'LinkedIn',
    'WhatsApp', 'Friend/Colleague', 'Email Newsletter',
    'Podcast', 'Other'
  ];

  const handleSaveUserInfo = async () => {
    if (!user) return;
    setLoading(true);
    try {
      // onboardingCompleted isn't set here — it only flips once the phone
      // verification step (next) is resolved (verified or explicitly
      // skipped), so a refresh mid-onboarding doesn't drop them straight
      // into the dashboard past that step.
      await update(ref(db, `users/${user.uid}`), {
        name: user.displayName || user.email?.split('@')[0],
        email: user.email,
        phone: phoneNumber,
        phoneVerified: false,
        profession: profession,
        location: location,
        industry: industry,
        hearAbout: hearAbout,
        plan: 'free',
        paymentStatus: 'pending',
        createdAt: Date.now()
      });
      setStep(2);
    } catch (error) {
      console.error('Error saving user info:', error);
      alert('Error saving information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Marks onboarding complete (verified either way — the skip path just
  // leaves phoneVerified false, which the dashboard banner then nudges
  // about) and fires the welcome WhatsApp message now that there's an
  // actual number on file to send it to.
  const finishOnboarding = async (verified: boolean) => {
    if (!user) return;
    await update(ref(db, `users/${user.uid}`), {
      phoneVerified: verified,
      onboardingCompleted: true,
      completedAt: Date.now(),
    });
    user.getIdToken().then((idToken: string) => {
      const apiBase = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
      return fetch(`${apiBase}/api/onboarding/complete`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
    }).catch(() => {});
    setStep(3);
  };

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setInterval(() => setOtpCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  const handleSendOtp = async () => {
    setOtpError('');
    if (!phoneNumber.trim()) { setOtpError('Enter your WhatsApp number first'); return; }
    setOtpSending(true);
    try {
      const res = await apiFetch('/api/account/phone/send-otp', { method: 'POST', body: JSON.stringify({ phone: phoneNumber }) });
      const data = await res.json();
      if (!data.success) { setOtpError(data.error || 'Could not send the code'); return; }
      setOtpSent(true);
      setOtpCooldown(60);
    } catch {
      setOtpError('Could not reach the server. Please try again.');
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError('');
    if (!otpCode.trim()) { setOtpError('Enter the code you received on WhatsApp'); return; }
    setOtpVerifying(true);
    try {
      const res = await apiFetch('/api/account/phone/verify-otp', { method: 'POST', body: JSON.stringify({ code: otpCode }) });
      const data = await res.json();
      if (!data.success) { setOtpError(data.error || 'Incorrect code'); return; }
      await finishOnboarding(true);
    } catch {
      setOtpError('Could not reach the server. Please try again.');
    } finally {
      setOtpVerifying(false);
    }
  };

  return (

 <>
      <SEO
        title="WhatsApp Bot Platform | Automate Customer Communication"
        description={`${companyName} helps Zimbabwean businesses automate WhatsApp communication, boost sales, and provide 24/7 customer support. Start your free trial today.`}
        keywords="WhatsApp business bot, Zimbabwe automation, customer service chatbot, AI messaging, business growth"
        type="website"
        noindex
      />

    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '48px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 20px 35px -10px rgba(0,0,0,0.2)'
      }}>
        {/* Progress Steps */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0', marginBottom: '20px' }}>
            {[1, 2, 3].map((s, i) => (
              <React.Fragment key={s}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: step >= s ? '#059669' : '#e5e7eb',
                  color: step >= s ? 'white' : '#9ca3af',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 'bold', fontSize: '16px', flexShrink: 0,
                  transition: 'background 0.3s'
                }}>
                  {step > s ? '✓' : s}
                </div>
                {i < 2 && (
                  <div style={{
                    height: '2px', width: '48px',
                    background: step > s ? '#059669' : '#e5e7eb',
                    transition: 'background 0.3s'
                  }} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>
              {step === 1 ? 'Tell us about yourself' : step === 2 ? 'Verify your WhatsApp number' : 'One last thing...'}
            </h2>
            <p style={{ color: '#6b7280', fontSize: '14px' }}>
              {step === 1
                ? 'Help us personalize your experience'
                : step === 2
                ? "We'll send a code there so draft-reply reviews, handoff alerts and other notifications reach you"
                : 'Would you like to explore the documentation before diving in?'}
            </p>
          </div>
        </div>

        {/* Step 1: User Info */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                What's your profession? <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select your profession</option>
                {professionsList.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                What industry are you in? <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select your industry</option>
                {industriesList.map(i => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Where are you located? <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, Country (e.g., Harare, Zimbabwe)"
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                WhatsApp Number <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+263 77 123 4567"
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                We'll send your invoices, payment reminders and account notifications here
              </p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                How did you hear about us? <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select
                value={hearAbout}
                onChange={(e) => setHearAbout(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '10px',
                  fontSize: '14px'
                }}
              >
                <option value="">Select an option</option>
                {hearAboutList.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 2: Verify WhatsApp number */}
        {step === 2 && (
          <div>
            <div style={{ padding: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', color: '#166534', marginBottom: '4px' }}>Sending a code to:</div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#065f46' }}>{phoneNumber}</div>
            </div>

            {!otpSent ? (
              <button
                onClick={handleSendOtp}
                disabled={otpSending}
                style={{
                  width: '100%', padding: '14px', background: '#059669', color: 'white',
                  border: 'none', borderRadius: '10px', cursor: otpSending ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold', fontSize: '15px', opacity: otpSending ? 0.7 : 1,
                }}
              >
                {otpSending ? 'Sending…' : 'Send verification code'}
              </button>
            ) : (
              <div>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px', textAlign: 'center' }}>
                  Tap the Copy Code button on WhatsApp, or type the code below.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  style={{
                    width: '100%', padding: '14px', border: '1px solid #d1d5db', borderRadius: '10px',
                    fontSize: '22px', textAlign: 'center', letterSpacing: '6px', marginBottom: '12px',
                  }}
                />
                <button
                  onClick={handleVerifyOtp}
                  disabled={otpVerifying || otpCode.length !== 6}
                  style={{
                    width: '100%', padding: '14px', background: '#059669', color: 'white',
                    border: 'none', borderRadius: '10px', cursor: 'pointer',
                    fontWeight: 'bold', fontSize: '15px', opacity: (otpVerifying || otpCode.length !== 6) ? 0.7 : 1, marginBottom: '10px',
                  }}
                >
                  {otpVerifying ? 'Verifying…' : 'Verify'}
                </button>
                <button
                  onClick={handleSendOtp}
                  disabled={otpSending || otpCooldown > 0}
                  style={{
                    width: '100%', padding: '10px', background: 'transparent', color: '#059669',
                    border: '1px solid #bbf7d0', borderRadius: '10px', cursor: otpCooldown > 0 ? 'not-allowed' : 'pointer',
                    fontWeight: '500', fontSize: '13px',
                  }}
                >
                  {otpCooldown > 0 ? `Resend code in ${otpCooldown}s` : 'Resend code'}
                </button>
              </div>
            )}

            {otpError && <p style={{ fontSize: '13px', color: '#dc2626', textAlign: 'center', marginTop: '10px' }}>{otpError}</p>}

            <p style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => finishOnboarding(false)}
                style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Skip for now — I'll verify later from my dashboard
              </button>
            </p>
          </div>
        )}

        {/* Step 3: Docs offer */}
        {step === 3 && (
          <div>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '8px'
            }}>
              {/* Option A — docs */}
              <button
                onClick={() => navigate('/documentation')}
                style={{
                  padding: '28px 20px', borderRadius: '16px',
                  border: '2px solid #059669', background: '#ecfdf5',
                  cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s'
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#d1fae5'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#ecfdf5'; }}
              >
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📚</div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#065f46', marginBottom: '6px' }}>
                  Yes, show me the docs
                </div>
                <div style={{ fontSize: '12px', color: '#047857', lineHeight: '1.5' }}>
                  Learn about bot setup, flows, integrations and best practices
                </div>
              </button>

              {/* Option B — build */}
              <button
                onClick={() => navigate('/create')}
                style={{
                  padding: '28px 20px', borderRadius: '16px',
                  border: '2px solid #d1d5db', background: '#f9fafb',
                  cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s'
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f3f4f6'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#f9fafb'; }}
              >
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>🚀</div>
                <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827', marginBottom: '6px' }}>
                  No, let's build
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.5' }}>
                  Jump straight in — you can always read the docs later
                </div>
              </button>
            </div>

            <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '16px' }}>
              You can access documentation any time from the sidebar
            </p>
          </div>
        )}

        {/* Navigation Buttons (step 1 only — steps 2 and 3 have their own CTAs) */}
        {step === 1 && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={() => {
                if (!profession || !industry || !location || !hearAbout || !phoneNumber.trim()) {
                  alert('Please fill in all required fields');
                  return;
                }
                handleSaveUserInfo();
              }}
              disabled={loading}
              style={{
                flex: 1, padding: '12px',
                background: '#059669', color: 'white',
                border: 'none', borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold', opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Complete Setup →'}
            </button>
          </div>
        )}
      </div>
    </div>
</>
  );
}
